import { useCallback, useEffect, useState } from 'react';
import { getVapidPublicKey, subscribePush, unsubscribePush } from '../../lib/api';

// The backend's unsubscribe route is keyed by our PushSubscription row id
// (not the browser endpoint), so we keep the last id we were handed —
// nothing sensitive, just enough to call DELETE /notifications/subscribe/:id.
const SUB_ID_KEY = 'egc-onboarding-push-sub-id';

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const buffer = new ArrayBuffer(rawData.length);
  const outputArray = new Uint8Array(buffer);
  for (let i = 0; i < rawData.length; i++) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

// ponytail: feature-detect only (serviceWorker + PushManager + Notification)
// — the dashboard's push slice adds iOS-version sniffing on top of this;
// skipped here since D16 push is a nice-to-have secondary channel, not the
// primary notification path (email covers that). Add iOS detection if staff
// actually hit this from iPhones in practice.
export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

type PushToggleStatus = 'idle' | 'busy' | 'error';

export function usePushToggle() {
  const supported = isPushSupported();
  const [subscribed, setSubscribed] = useState(false);
  const [status, setStatus] = useState<PushToggleStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!supported) return;
    let cancelled = false;
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => {
        if (!cancelled) setSubscribed(sub !== null);
      })
      .catch(() => {
        if (!cancelled) setSubscribed(false);
      });
    return () => {
      cancelled = true;
    };
  }, [supported]);

  // enable/disable resolve to whether they succeeded (rather than rejecting)
  // so a caller can toast on the actual outcome without waiting on a render
  // to see the updated `status`/`error` state.
  const enable = useCallback(async (): Promise<boolean> => {
    setStatus('busy');
    setError(null);
    try {
      const { publicKey } = await getVapidPublicKey();
      if (!publicKey) throw new Error('Push notifications are not configured on the server yet.');

      const permission = await Notification.requestPermission();
      if (permission !== 'granted') throw new Error('Notifications blocked in your browser.');

      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
      const subJson = sub.toJSON() as { endpoint: string; keys?: { p256dh: string; auth: string } };
      if (!subJson.keys) throw new Error('Browser subscription is missing encryption keys.');

      const saved = await subscribePush({
        endpoint: subJson.endpoint,
        p256dh: subJson.keys.p256dh,
        auth: subJson.keys.auth,
        userAgent: navigator.userAgent,
      });
      localStorage.setItem(SUB_ID_KEY, saved.id);
      setSubscribed(true);
      setStatus('idle');
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to enable notifications.');
      setStatus('error');
      return false;
    }
  }, []);

  const disable = useCallback(async (): Promise<boolean> => {
    setStatus('busy');
    setError(null);
    try {
      const savedId = localStorage.getItem(SUB_ID_KEY);
      if (savedId) {
        await unsubscribePush(savedId).catch(() => undefined); // best-effort — still unsubscribe locally
        localStorage.removeItem(SUB_ID_KEY);
      }
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      await sub?.unsubscribe();
      setSubscribed(false);
      setStatus('idle');
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to disable notifications.');
      setStatus('error');
      return false;
    }
  }, []);

  return { supported, subscribed, status, error, enable, disable };
}
